import { useTranslation } from 'react-i18next';
import type { BillableServiceView, InvoiceLineView } from '@lims/shared-types';
import {
  ActionButton,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from '../../components/ui/index.js';
import { displayName } from '../clients/bilingual.js';
import type { Locale } from '@lims/shared-types';

/**
 * The invoice line editor, shared by opening a draft (`InvoiceFormPage`) and
 * editing one in place (`InvoiceDetailPage`).
 *
 * PRICES COME FROM THE WORK ORDER. The unit-price field stays EMPTY by
 * default for a billed line — typing one is an override the server checks
 * against `pricing.override`, and the reason is asked for here rather than
 * discovered after a refusal (mirrors `QuotationFormPage`). A MANUAL line has
 * no frozen price to default to, so its description and price are typed by
 * hand.
 */
export interface LineDraft {
  readonly key: string;
  workOrderServiceId: string | null;
  label: string;
  remaining: string | null;
  quantity: number | null;
  unitPrice: number | null;
  priceOverrideReason: string;
  discountPercent: number | null;
  descriptionAr: string;
  descriptionEn: string;
}

let lineKeySeed = 0;
function newLineKey(): string {
  lineKeySeed += 1;
  return `line-${lineKeySeed}-${Date.now()}`;
}

export function lineFromService(
  service: BillableServiceView,
  locale: Locale,
): LineDraft {
  return {
    key: newLineKey(),
    workOrderServiceId: service.id,
    label: `${displayName(service.description, locale) ?? service.itemCode ?? ''} — ${service.workOrder.number}`,
    remaining: service.remainingQuantity,
    quantity: Number(service.remainingQuantity) || null,
    unitPrice: null,
    priceOverrideReason: '',
    discountPercent: null,
    descriptionAr: '',
    descriptionEn: '',
  };
}

/** An existing invoice line, as the draft editor starts from it. */
export function lineFromExisting(line: InvoiceLineView): LineDraft {
  return {
    key: newLineKey(),
    workOrderServiceId: line.workOrderService?.id ?? null,
    label: line.workOrderService
      ? `${displayName(line.description, 'en') ?? line.itemCode ?? ''} — ${line.workOrderService.workOrderNumber}`
      : '',
    remaining: null,
    quantity: Number(line.quantity) || null,
    unitPrice: line.priceSource === 'override' ? line.unitPriceMinor / 100 : null,
    priceOverrideReason: line.priceOverrideReason ?? '',
    discountPercent: line.discount.percent === null ? null : Number(line.discount.percent),
    descriptionAr: line.workOrderService ? '' : (line.description.ar ?? ''),
    descriptionEn: line.workOrderService ? '' : (line.description.en ?? ''),
  };
}

export function emptyManualLine(): LineDraft {
  return {
    key: newLineKey(),
    workOrderServiceId: null,
    label: '',
    remaining: null,
    quantity: 1,
    unitPrice: null,
    priceOverrideReason: '',
    discountPercent: null,
    descriptionAr: '',
    descriptionEn: '',
  };
}

/** The body's `lines` array, exactly as `invoiceLineInputSchema` expects it. */
export function linePayload(lines: readonly LineDraft[]) {
  return lines
    .filter((line) => line.workOrderServiceId || line.quantity)
    .map((line) => ({
      ...(line.workOrderServiceId
        ? { workOrderServiceId: line.workOrderServiceId }
        : {}),
      ...(line.descriptionAr.trim() ? { descriptionAr: line.descriptionAr.trim() } : {}),
      ...(line.descriptionEn.trim() ? { descriptionEn: line.descriptionEn.trim() } : {}),
      quantity: line.quantity as number,
      ...(line.unitPrice === null ? {} : { unitPriceMinor: Math.round(line.unitPrice * 100) }),
      ...(line.priceOverrideReason.trim()
        ? { priceOverrideReason: line.priceOverrideReason.trim() }
        : {}),
      ...(line.discountPercent === null ? {} : { discountPercent: line.discountPercent }),
    }));
}

export function InvoiceLineEditor({
  lines,
  setLines,
  availableServices,
  pickedServiceId,
  setPickedServiceId,
  currency,
  canOverride,
  locale,
}: {
  readonly lines: readonly LineDraft[];
  readonly setLines: (update: (current: LineDraft[]) => LineDraft[]) => void;
  readonly availableServices: readonly BillableServiceView[];
  readonly pickedServiceId: string | null;
  readonly setPickedServiceId: (id: string | null) => void;
  readonly currency?: string;
  readonly canOverride: boolean;
  readonly locale: Locale;
}) {
  const { t } = useTranslation();

  const updateLine = (key: string, patch: Partial<LineDraft>) =>
    setLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...patch } : line)),
    );

  return (
    <div className="grid gap-gutter">
      {availableServices.length > 0 || pickedServiceId ? (
        <div className="grid min-w-0 grid-cols-1 gap-stack sm:grid-cols-[minmax(0,1fr)_auto]">
          <SelectField
            label={t('billing.addBilledService')}
            placeholder={t('billing.chooseBilledService')}
            offerNoChoice={false}
            value={pickedServiceId}
            onChange={setPickedServiceId}
            options={availableServices.map((service) => ({
              value: service.id,
              label: `${displayName(service.description, locale) ?? service.itemCode ?? ''} — ${service.workOrder.number}`,
              description: t('billing.ofQuantity', {
                quantity: service.remainingQuantity,
              }),
            }))}
          />
          <div className="flex items-end gap-inline">
            <ActionButton
              priority="secondary"
              isDisabled={!pickedServiceId}
              onPress={() => {
                const service = availableServices.find(
                  (candidate) => candidate.id === pickedServiceId,
                );
                if (!service) return;
                setLines((current) => [...current, lineFromService(service, locale)]);
                setPickedServiceId(null);
              }}
            >
              {t('billing.addBilledService')}
            </ActionButton>
          </div>
        </div>
      ) : null}

      <div>
        <ActionButton
          priority="tertiary"
          size="sm"
          onPress={() => setLines((current) => [...current, emptyManualLine()])}
        >
          {t('invoices.addManualLine')}
        </ActionButton>
      </div>

      {lines.map((line, index) => (
        // One block per line: what it bills and its removal in a header
        // strip, the line's fields beneath (D278).
        <div key={line.key} className="min-w-0 rounded-medium border border-default-200">
          <div className="flex min-h-10 flex-wrap items-center gap-inline border-b border-default-200 bg-default-50 px-3 py-1.5">
            <div className="min-w-0">
              <p className="text-caption font-semibold text-default-800">
                {line.workOrderServiceId ? line.label : t('ordersRecord.lineNumber', { n: index + 1 })}
              </p>
              {line.workOrderServiceId && line.remaining ? (
                <p className="measure text-caption text-default-600" dir="ltr">
                  {t('billing.ofQuantity', { quantity: line.remaining })}
                </p>
              ) : null}
            </div>
            <span className="ms-auto">
              <ActionButton
                priority="tertiary"
                size="sm"
                onPress={() =>
                  setLines((current) =>
                    current.filter((candidate) => candidate.key !== line.key),
                  )
                }
              >
                {t('quotations.removeLine')}
              </ActionButton>
            </span>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-stack p-3 sm:grid-cols-2">
            {line.workOrderServiceId ? null : (
              <>
                <TextField
                  label={t('invoices.descriptionAr')}
                  value={line.descriptionAr}
                  onChange={(value) => updateLine(line.key, { descriptionAr: value })}
                />
                <TextField
                  label={t('invoices.descriptionEn')}
                  value={line.descriptionEn}
                  onChange={(value) => updateLine(line.key, { descriptionEn: value })}
                />
              </>
            )}

            <NumberField
              label={t('invoices.quantity')}
              value={line.quantity}
              onChange={(value) => updateLine(line.key, { quantity: value })}
              step="0.001"
            />

            {line.workOrderServiceId && !canOverride ? null : (
              <NumberField
                label={t('invoices.unitPrice')}
                description={
                  line.workOrderServiceId ? t('invoices.unitPriceOverrideHint') : undefined
                }
                value={line.unitPrice}
                onChange={(value) => updateLine(line.key, { unitPrice: value })}
                step="0.01"
                {...(currency ? { unit: currency } : {})}
                isRequired={!line.workOrderServiceId}
              />
            )}

            {line.workOrderServiceId && line.unitPrice !== null ? (
              <TextAreaField
                label={t('invoices.priceOverrideReason')}
                value={line.priceOverrideReason}
                onChange={(value) => updateLine(line.key, { priceOverrideReason: value })}
                rows={2}
                isRequired
              />
            ) : null}

            <NumberField
              label={t('invoices.lineDiscount')}
              value={line.discountPercent}
              onChange={(value) => updateLine(line.key, { discountPercent: value })}
              step="0.01"
            />

          </div>
        </div>
      ))}
      {lines.length === 0 ? (
        <p className="text-body text-default-600">{t('invoices.noLinesYet')}</p>
      ) : null}
    </div>
  );
}
